import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { batch, useDispatch, useSelector } from 'react-redux'
import { formatOnlyNumbers, Spinner, useAnalyticsPageView, useEffectOnce } from '@chilecompra/react-kit'
import { Button, Input, useInput, useSelect, useViewport } from '@chilecompra/react-kit/components'
import { BUSINESS_TYPES_OPTIONS, PHONE_NUMBERS_STARTS_WITH } from '../../../config/settings/constants'
import {
  onCleanGuestBusinessThunk,
  onCreateTemporaryBuyerThunk,
  onGetGuestBusinessOptionsThunk,
  registerFlowThunk
} from '../RegisterWizard.actions'
import {
  Root,
  DesktopNavigation,
  MobileNavigation,
  Section,
  Strong,
  Title,
  Instructions,
  Divider,
  SubInput,
  MobileButtons
} from './RegisterWizardBusinessInformation.styles'
import { Link } from '@mui/material'
import { PhoneNumberPrefix } from '../Step1/RegisterWizardPersonalInformation.styles'
import {
  checkInputErrorEmail,
  checkInputErrorMaxLength,
  checkInputErrorMinLength,
  checkInputErrorOnlyNumbers,
  checkInputErrorRequired
} from '@chilecompra/react-kit/utils'
import { checkInputErrorStartsWith, checkInputErrorUrl } from '../../../modules/utils/checkers'
import { REACT_APP_SII_URL } from '../../../config/settings/environment'

/**
 * The RegisterWizardBusinessInformation's container.
 */
const RegisterWizardBusinessInformation = props => {
  const { onChangeStep } = props
  const dispatch = useDispatch()
  const { business, loading } = useSelector(state => state.registerWizard)
  const { size } = useViewport()
  const flow = 'INICIO_PASO_3_BIS'

  const businessTypeName = BUSINESS_TYPES_OPTIONS.find(option => option.value === business?.type)?.name || ''

  const { value: typeValue, onChange: handleTypeChange } = useSelect({
    initialValue: businessTypeName,
    overwriteCallback: () => businessTypeName
  })
  const { value: addressValue, onChange: handleAddressChange } = useInput({
    initialValue: business?.address,
    overwriteCallback: () => business?.address
  })

  const { value: districtValue, onChange: handleDistrictChange } = useSelect({
    initialValue: business?.district?.name,
    overwriteCallback: () => business?.district?.name
  })
  const { value: stateValue, onChange: handleStateChange } = useSelect({
    initialValue: business?.state?.name,

    overwriteCallback: () => business?.state?.name
  })

  const {
    error: phoneError,
    value: phoneValue,
    onChange: handlePhoneChange
  } = useInput({
    initialValue: '',
    errorCallbacks: [
      checkInputErrorRequired(),
      checkInputErrorStartsWith('El número debe comenzar con 9 o 2.', PHONE_NUMBERS_STARTS_WITH),
      checkInputErrorOnlyNumbers(),
      checkInputErrorMinLength(9, 'Este campo permite 8 dígitos.'),
      checkInputErrorMaxLength(9, 'Este campo permite 8 dígitos.')
    ],
    formatCallbacks: [formatOnlyNumbers]
  })

  const {
    error: emailError,
    value: emailValue,
    onChange: handleEmailChange
  } = useInput({
    errorCallbacks: [
      checkInputErrorRequired('Debes ingresar tu dirección de correo.'),
      checkInputErrorEmail('Tu dirección de correo parece estar en un formato incorrecto.'),
      checkInputErrorMaxLength(80, 'Tu dirección de correo excede el máximo de 80 carácteres permitidos.')
    ],
    initialValue: ''
  })

  const {
    error: websiteError,
    value: websiteValue,
    onChange: handleWebsiteChange
  } = useInput({
    initialValue: '',
    errorCallbacks: [checkInputErrorUrl('Por favor ingrese una URL válida.')]
  })

  const handlePrev = () =>
    dispatch(
      onCleanGuestBusinessThunk({
        callback: () => onChangeStep('business-search')
      })
    )

  const handleNext = () =>
    dispatch(
      onCreateTemporaryBuyerThunk({
        address: addressValue,
        district: business?.district,
        email: emailValue,
        nickname: business?.name || '',
        phone: phoneValue,
        state: business?.state,
        website: websiteValue,
        callback: () => onChangeStep('representative-authority')
      })
    )

  const disabledNext = useMemo(() => {
    if (business?.entityType === 'natural') {
      return (
        addressValue === '' ||
        districtValue === '' ||
        stateValue === '' ||
        phoneValue === '' ||
        emailValue === '' ||
        websiteValue === '' ||
        phoneError ||
        emailError ||
        websiteError ||
        loading
      )
    }

    return (
      addressValue === '' ||
      districtValue === '' ||
      stateValue === '' ||
      phoneValue === '' ||
      websiteValue === '' ||
      emailValue === '' ||
      phoneError ||
      emailError ||
      websiteError ||
      loading
    )
  }, [
    addressValue,
    business?.entityType,
    districtValue,
    loading,
    stateValue,
    emailValue,
    websiteValue,
    phoneError,
    emailError,
    websiteError,
    phoneValue
  ])

  useEffectOnce(() => {
    batch(() => {
      dispatch(registerFlowThunk({ flow }))
      dispatch(onGetGuestBusinessOptionsThunk())
    })
  })

  useAnalyticsPageView('/paso/3/informacion_empresa')
  return (
    <Root>
      <Section item>
        <Title tabIndex="0" aria-label="Paso 3: Revisa y completa los datos de tu institución">
          Revisa y completa los datos de tu institución
        </Title>
      </Section>

      {business?.loading && (
        <Section
          role="status"
          aria-label="Cargando..."
          display="flex"
          justifyContent="center"
          alignItems="center"
          margin="90px auto"
        >
          <Spinner />
        </Section>
      )}
      {!business?.loading && (
        <Section>
          <Section item margin="23px 0 33px" maxWidth="683px">
            <Instructions tabIndex="0">
              <Strong>1.</Strong> Esta es la información asociada al RUT <Strong>{business?.dni}</Strong> según el
              registro del Servicio de Impuestos Internos. Si deseas actualizarla debes hacerlo directamente en{' '}
              <Link
                href={REACT_APP_SII_URL}
                aria-label="Ir al Servicio de Impuestos interno"
                tabIndex="0"
                target="_blank"
                rel="noopener noreferrer"
              >
                www.sii.cl
              </Link>
            </Instructions>
          </Section>
          <Section container margin="3px 0 0" maxWidth="693px">
            <Section item xs={12}>
              <Input
                disabled
                loading={business?.loading}
                label="Razón social"
                aria-label="Razón social"
                value={business?.name || ''}
              />
            </Section>
          </Section>
          <Section container maxWidth="693px">
            <Section item lg={12} xs={12}>
              <Input
                disabled
                label="Tipo de institución"
                aria-label="Tipo de institución"
                loading={business?.loading}
                options={BUSINESS_TYPES_OPTIONS}
                value={typeValue}
                onChange={handleTypeChange}
              />
            </Section>
          </Section>
          <Section container margin={business?.entityType === 'legal' ? '8px 0 0' : '3px 0 0'} maxWidth="693px">
            <Input
              disabled
              label="Domicilio legal"
              aria-label="Domicilio legal"
              loading={business?.loading}
              value={addressValue}
              onChange={handleAddressChange}
            />
          </Section>
          <Section container margin="3px 0 0" maxWidth="693px">
            <Section item md={6} padding={!size.isDownToMedium && '0 21px 0 0'} xs={12}>
              <Input
                disabled
                label="Región"
                aria-label="Región"
                loading={business?.loading}
                options={business?.states || []}
                value={stateValue}
                onChange={handleStateChange}
              />
            </Section>
            <Section item md={6} padding={!size.isDownToMedium && '0 0 0 21px'} xs={12}>
              <Input
                disabled
                label="Comuna"
                aria-label="Comuna"
                loading={business?.loading || business?.districtLoading}
                options={business?.districts || []}
                value={districtValue}
                onChange={handleDistrictChange}
              />
            </Section>
          </Section>
          <Section margin="30px 0">
            <Divider />
          </Section>
          <Section item margin="52px 0" maxWidth="683px">
            <Instructions tabIndex="0">
              <Strong>2.</Strong> Completa con los siguientes datos de tu institución, estos serán visibles en tu ficha
              de organismo comprador.
            </Instructions>
          </Section>

          <Section container margin="3px 0 0" maxWidth="693px" gap={size.isTiny && '10px'}>
            <Section item md={6} xs={12} margin={size.isTiny && '-15px 0 0 0'}>
              <Input
                error={websiteError}
                value={websiteValue}
                onChange={handleWebsiteChange}
                label="Sitio web"
                aria-label="Sitio web"
              />
              {!websiteError && <SubInput>Ejemplo:www.chilecompra.cl</SubInput>}
            </Section>
            <Section item md={6} padding={!size.isDownToMedium && '0 0 0 21px'} xs={12}>
              <Input
                value={phoneValue}
                error={phoneError}
                onChange={handlePhoneChange}
                prefix={<PhoneNumberPrefix>+56</PhoneNumberPrefix>}
                label="Teléfono de la institución"
                aria-label="Teléfono de la institución"
              />
              {!phoneError && <SubInput>Ejemplo: + 56 2 1234 5678</SubInput>}
            </Section>
          </Section>

          <Section container margin="20px 0 " maxWidth="693px">
            <Section item md={6} padding={!size.isDownToMedium && '0 21px 0 0'} xs={12}>
              <Input
                error={emailError}
                label="Correo electrónico laboral"
                aria-label="Correo electrónico laboral"
                value={emailValue}
                onChange={handleEmailChange}
              />
              {!emailError && <SubInput>Ejemplo: dccp@chilecompra.cl</SubInput>}
            </Section>
          </Section>

          {!size.isTiny && (
            <Section maxWidth="693px" margin="72px 0 0 0">
              <DesktopNavigation>
                <Button
                  tabIndex="0"
                  aria-label="Cancelar"
                  type="button"
                  variant="outlined"
                  width="156px"
                  onClick={handlePrev}
                >
                  Cancelar
                </Button>
                <Button
                  tabIndex="0"
                  aria-label="Siguiente"
                  color="primary"
                  disabled={disabledNext}
                  loading={loading}
                  margin="0 0 0 30px"
                  type="button"
                  width="159px"
                  onClick={handleNext}
                >
                  Siguiente
                </Button>
              </DesktopNavigation>
            </Section>
          )}

          {size.isTiny && (
            <Section container>
              <MobileNavigation>
                <MobileButtons tabIndex="0" aria-label="Cancelar" onClick={handlePrev} type="button" variant="outlined">
                  Cancelar
                </MobileButtons>
                <MobileButtons
                  tabIndex="0"
                  aria-label="Siguiente"
                  color="primary"
                  disabled={disabledNext}
                  loading={loading}
                  type="button"
                  onClick={handleNext}
                >
                  Siguiente
                </MobileButtons>
              </MobileNavigation>
            </Section>
          )}
        </Section>
      )}
    </Root>
  )
}

RegisterWizardBusinessInformation.defaultProps = {
  onChangeStep: () => undefined
}

RegisterWizardBusinessInformation.propTypes = {
  onChangeStep: PropTypes.func
}

export default RegisterWizardBusinessInformation
